import React, { useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  FileText,
  Users,
  BarChart,
  Building,
  Building2,
  ClipboardList,
  Upload,
  LogOut,
  User,
  Eye,
  Calendar,
  CalendarCheck,
  Database,
  Bell,
  Search,
  HelpCircle
} from 'lucide-react';
import clsx from 'clsx';
import { ReactComponent as Logo } from '@assets/logo.svg';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { ROLES } from '@app-types';
import { hasRole } from '@utils/permissionUtils';
import { formatPersonName } from '@utils/stringUtils';
import Tooltip from '@components/common/Tooltip';
import styles from './Sidebar.module.css';

interface MenuItem {
  id: string;
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  path: string;
  requiredPermissions?: string[];
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const permissions = usePermissions();

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const menuItems: MenuItem[] = useMemo(
    () => {
      const isContractRole = hasRole(user, ROLES.CONTRACT_ROLE);
      const hasDashboardRole = hasRole(user, [
        ROLES.PROGRAM_LEAD,
        ROLES.EXECUTIVE,
        ROLES.ASSESSOR,
        ROLES.ADMIN,
        ROLES.SCHEDULER,
        ROLES.VIEW_ONLY,
        ROLES.CONTRACT_ROLE
      ]);
      const showDashboard = hasDashboardRole;
      const showEvidence = isContractRole;

      const items: MenuItem[] = [];

      if (showDashboard) {
        items.push({
          id: 'dashboard',
          icon: Home,
          label: 'Dashboard',
          path: '/dashboard'
        });
      }

      if (showEvidence) {
        items.push({
          id: 'evidence',
          icon: Upload,
          label: 'Evidence',
          path: '/evidence',
          requiredPermissions: ['isContractRole']
        });
      }

      items.push(
        {
          id: 'scheduler',
          icon: Calendar,
          label: 'Schedule Meetings',
          path: '/scheduler',
          requiredPermissions: ['canAccessScheduler']
        },
        {
          id: 'assessor-meetings',
          icon: CalendarCheck,
          label: 'Assessment Meetings',
          path: '/assessor/meetings',
          requiredPermissions: ['isAssessor', 'isAdmin']
        },
        {
          id: 'evidence-review',
          icon: Eye,
          label: 'Evidence Review',
          path: '/evidence-review',
          requiredPermissions: ['isAssessor']
        },
        {
          id: 'assessments',
          icon: ClipboardList,
          label: 'Assessments',
          path: '/assessments',
          requiredPermissions: ['canAccessAssessments']
        },
        {
          id: 'contracts',
          icon: FileText,
          label: 'Contracts',
          path: '/contracts',
          requiredPermissions: ['canAccessContracts']
        },
        {
          id: 'suppliers',
          icon: Building,
          label: 'Suppliers',
          path: '/suppliers',
          requiredPermissions: ['canAccessSuppliers']
        },
        {
          id: 'users',
          icon: Users,
          label: 'Users',
          path: '/users',
          requiredPermissions: ['canAccessUsers']
        },
        {
          id: 'divisions',
          icon: Building2,
          label: 'Divisions',
          path: '/divisions',
          requiredPermissions: ['canAccessDivisions']
        },
        {
          id: 'notification-settings',
          icon: Bell,
          label: 'Notifications',
          path: '/admin/notifications',
          requiredPermissions: ['isAdminStrict']
        },
        {
          id: 'queries',
          icon: Search,
          label: 'Queries',
          path: '/queries',
          requiredPermissions: ['canAccessQueries']
        },
        {
          id: 'reports',
          icon: BarChart,
          label: 'Reports',
          path: '/reports',
          requiredPermissions: ['canAccessReports']
        },
        {
          id: 'data-management',
          icon: Database,
          label: 'Data Management',
          path: '/admin/data-management',
          requiredPermissions: ['isAdminStrict']
        },
        {
          id: 'help',
          icon: HelpCircle,
          label: 'Help',
          path: '/help'
        }
      );

      return items;
    },
    [user]
  );

  // Filter menu items based on user permissions
  const visibleMenuItems = useMemo(
    () =>
      menuItems.filter((item) => {
        if (!item.requiredPermissions || item.requiredPermissions.length === 0) {
          return true;
        }

        return item.requiredPermissions.some((permission) => {
          switch (permission) {
            case 'canAccessContracts':
              return permissions.canAccessContracts;
            case 'canAccessSuppliers':
              return permissions.canAccessSuppliers;
            case 'canAccessUsers':
              return permissions.canAccessUsers;
            case 'canAccessDivisions':
              return permissions.canAccessDivisions;
            case 'canAccessReports':
              return permissions.canAccessReports;
            case 'canAccessScheduler':
              return permissions.canAccessScheduler;
            case 'canAccessAssessments':
              return permissions.canAccessAssessments;
            case 'canAccessQueries':
              return permissions.canAccessQueries;
            case 'isContractRole':
              return permissions.isContractRole;
            case 'isContractManager':
              return permissions.isContractManager;
            case 'isAssessor':
              return permissions.isAssessor;
            case 'isAdmin':
              return permissions.isAdmin;
            case 'isAdminStrict':
              return permissions.isAdminStrict;
            default:
              return false;
          }
        });
      }),
    [menuItems, permissions]
  );

  const isActiveItem = useCallback(
    (path: string) => {
      // Prefix match on a trailing slash so `/evidence` does not highlight for `/evidence-review`.
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    },
    [location.pathname]
  );

  const handleItemClick = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarLogo}>
        <Logo width={32} height={32} className={styles.sidebarLogoImage} aria-label="RZ Strategies" />
      </div>

      <nav className={styles.sidebarNav}>
        {visibleMenuItems.map((item) => {
          const Icon = item.icon;
          return (
            <Tooltip key={item.id} content={item.label} position="right" delay={200} gap={12}>
              <button
                type="button"
                aria-label={item.label}
                data-test-id={`sidebar-item-${item.id}`}
                className={clsx(styles.sidebarItem, {
                  [styles.sidebarItemActive]: isActiveItem(item.path)
                })}
                onClick={() => handleItemClick(item.path)}
              >
                <Icon size={24} />
              </button>
            </Tooltip>
          );
        })}
      </nav>

      <div className={styles.sidebarFooter}>
        {user && (
          <Tooltip
            content={
              <div>
                <strong>
                  {formatPersonName(user)}
                </strong>
                <br />
                <span style={{ opacity: 0.8 }}>{user.roles?.join(', ') || 'Contact'}</span>
              </div>
            }
            position="right"
            delay={200}
            gap={12}
          >
            <button className={clsx(styles.sidebarItem, styles.sidebarUser)}>
              <User size={24} />
            </button>
          </Tooltip>
        )}
        <Tooltip content="Logout" position="right" delay={200} gap={12}>
          <button className={clsx(styles.sidebarItem, styles.sidebarLogout)} onClick={handleLogout}>
            <LogOut size={24} />
          </button>
        </Tooltip>
      </div>
    </aside>
  );
};

export default Sidebar;
